import { QuestionCircleOutlined } from '@ant-design/icons';
import { App, Collapse, Form, Input, Select, Tooltip } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import { Roles } from '@/core/utils/roles';
import { rolesInfoData } from '@/core/utils/rolesInfo';
import { getCreateUserErrorMessage, logError } from '@/utils/formatError';
import { validateTelegramUsername } from '@/utils/validators';
import { useCreateUser, useGetUserMeta } from '../query/users.hooks';

const { Option } = Select;

export const NewUserModalDocumentType = {
  dni: 'dni',
  ce: 'ce',
  ptp: 'ptp',
} as const;

interface NewUserModalProps {
  visible: boolean;
  onCancel: () => void;
}

type DocumentType = keyof typeof NewUserModalDocumentType;

interface NewUserFormValues {
  name: string;
  email: string;
  telegram?: string;
  fullname: string;
  documentType: DocumentType;
  metadata?: {
    dni?: string;
    ce?: string;
    ptp?: string;
  };
  departmentIds: number[];
  roles: string[];
}

export const NewUserModalView: React.FC<NewUserModalProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm<NewUserFormValues>();
  const { message: messageApi } = App.useApp();
  const { data: userMeta } = useGetUserMeta({});
  const { mutateAsync: createUser, isPending: createUserLoading } = useCreateUser();
  const [documentType, setDocumentType] = useState<keyof typeof NewUserModalDocumentType>(
    NewUserModalDocumentType.dni
  );
  const [rolesInfoVisible, setRolesInfoVisible] = useState(false);

  const initialValues = useMemo(
    () => ({
      documentType: NewUserModalDocumentType.dni,
      roles: [Roles.driver],
    }),
    []
  );

  useEffect(() => {
    if (visible) {
      setDocumentType(NewUserModalDocumentType.dni);
      form.setFieldsValue(initialValues);
    }
  }, [visible, form, initialValues]);

  const handleSubmit = useCallback(
    async (values: NewUserFormValues) => {
      try {
        await form.validateFields();
        const currentDocumentType = values.documentType || documentType;
        const metadata = {
          fullname: values.fullname,
          ...(currentDocumentType === NewUserModalDocumentType.dni && {
            dni: values.metadata?.dni,
          }),
          ...(currentDocumentType === NewUserModalDocumentType.ce && { ce: values.metadata?.ce }),
          ...(currentDocumentType === NewUserModalDocumentType.ptp && {
            ptp: values.metadata?.ptp,
          }),
        };
        await createUser({
          email: values.email,
          name: values.name,
          roles: values.roles,
          departmentIds: values.departmentIds,
          departmentId: values.departmentIds[0],
          metadata,
          telegramName: values.telegram || '',
        });
        messageApi.success(`User [${values.name}] has been created!`);
        form.resetFields();
        setDocumentType(NewUserModalDocumentType.dni);
        onCancel();
      } catch (error) {
        logError(error, 'Create user');
        messageApi.error(getCreateUserErrorMessage(error));
      }
    },
    [createUser, documentType, form, onCancel, messageApi]
  );

  const documentOptions = useMemo(() => Object.keys(NewUserModalDocumentType), []);

  return (
    <AppModal
      open={visible}
      confirmLoading={createUserLoading}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="Create"
    >
      <h2>New User</h2>
      <Form form={form} onFinish={handleSubmit} initialValues={initialValues}>
        <Form.Item
          className="app-form-item"
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter the name' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          className="app-form-item"
          label="E-mail"
          name="email"
          rules={[
            { required: true, message: 'Please enter the e-mail' },
            { type: 'email', message: 'Invalid email format' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          className="app-form-item"
          label="Telegram"
          name="telegram"
          rules={[{ required: false }, { validator: validateTelegramUsername }]}
        >
          <Input placeholder="@username" />
        </Form.Item>
        <Form.Item
          className="app-form-item"
          label="Departments"
          name="departmentIds"
          rules={[{ required: true, message: 'Please select at least one department' }]}
        >
          <Select mode="multiple" style={{ width: '100%' }} placeholder="Select department(s)">
            {userMeta?.department?.map((department: { id: number; name: string }) => (
              <Option key={department.id} value={department.id}>
                {department.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          className="app-form-item"
          label="Fullname"
          name="fullname"
          rules={[{ required: true, message: 'Please enter the fullname' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item className="app-form-item" label="Document" name="documentType">
          <Select onChange={setDocumentType} style={{ width: '100%' }}>
            {documentOptions.map((doc) => (
              <Option key={doc} value={doc}>
                {doc.toUpperCase()}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {documentType === NewUserModalDocumentType.dni && (
          <FormItem
            className="app-form-item"
            label="DNI"
            name={['metadata', 'dni']}
            rules={[{ required: true, message: 'Please enter the DNI' }]}
          >
            <Input />
          </FormItem>
        )}
        {documentType === NewUserModalDocumentType.ce && (
          <FormItem
            className="app-form-item"
            label="CE"
            name={['metadata', 'ce']}
            rules={[{ required: true, message: 'Please enter the CE' }]}
          >
            <Input />
          </FormItem>
        )}
        {documentType === NewUserModalDocumentType.ptp && (
          <FormItem
            className="app-form-item"
            label="PTP"
            name={['metadata', 'ptp']}
            rules={[{ required: true, message: 'Please enter the PTP' }]}
          >
            <Input />
          </FormItem>
        )}
        <FormItem
          className="app-form-item"
          label={
            <span>
              Roles
              <Tooltip title="Click to view role descriptions" placement="left">
                <QuestionCircleOutlined
                  style={{ marginLeft: 6, color: '#888', cursor: 'pointer' }}
                  onClick={() => setRolesInfoVisible(true)}
                />
              </Tooltip>
            </span>
          }
          name="roles"
          rules={[{ required: true, message: 'Please select at least one role' }]}
        >
          <Select mode="multiple" style={{ width: '100%' }} placeholder="Select role(s)">
            <Option value={Roles.driver}>Driver</Option>
            <Option value={Roles.operator}>Operator</Option>
            <Option value={Roles.seniorOperator}>Senior Operator</Option>
            <Option value={Roles.auditor}>Auditor</Option>
            <Option value={Roles.accountant}>Accountant</Option>
            <Option value={Roles.financeManager}>Finance Manager</Option>
            <Option value={Roles.storekeeper}>Storekeeper</Option>
            <Option value={Roles.manager}>Manager</Option>
          </Select>
        </FormItem>
        <AppModal
          open={rolesInfoVisible}
          onCancel={() => setRolesInfoVisible(false)}
          title="Roles description"
          footer={null}
        >
          <Collapse
            accordion
            defaultActiveKey={rolesInfoData[0]?.title}
            items={rolesInfoData.map((role) => ({
              key: role.title,
              label: role.title,
              children: (
                <div className="ant-list ant-list-sm">
                  <div className="ant-list-items">
                    {role.items.map((item) => (
                      <div className="ant-list-item" key={item}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            }))}
          />
        </AppModal>
      </Form>
    </AppModal>
  );
};
